import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserPlus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit3, 
  Trash2, 
  User, 
  Mail, 
  Shield,
  Building2,
  Clock,
  AlertTriangle,
  Ban,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { http } from '../../lib/api';
import UsuarioForm from './UsuarioForm';
import SmartAdaptiveList from '../ui/SmartAdaptiveList';

export default function UsuariosList() {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRol, setFilterRol] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    if (user?.rol === 'admin') {
      cargarDatos();
    }
  }, [user]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      await Promise.all([cargarUsuarios(), cargarClientes()]);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarUsuarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/usuarios', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsuarios(response.data || []);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
  };

  const cargarClientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/clientes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const handleNuevoUsuario = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditarUsuario = (usuario) => {
    setEditingUser(usuario);
    setShowForm(true);
  };

  const handleEliminarUsuario = (usuario) => {
    setUserToDelete(usuario);
    setShowDeleteModal(true);
  };

  const handleToggleEstado = async (usuario) => {
    try {
      const token = localStorage.getItem('token');
      const nuevoEstado = !usuario.activo;
      
      await http.put(`/usuarios/${usuario._id}`, 
        { activo: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await cargarUsuarios();
    } catch (error) {
      console.error('Error cambiando estado de usuario:', error);
      alert('Error al cambiar estado: ' + (error.response?.data?.detail || error.message));
    }
  };

  const confirmarEliminacion = async () => {
    if (!userToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await http.delete(`/usuarios/${userToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await cargarUsuarios();
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      alert('Error al eliminar el usuario: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleFormSave = async () => {
    setShowForm(false);
    setEditingUser(null);
    await cargarUsuarios();
  };

  // Funciones de utilidad
  const obtenerNombreCliente = (clienteId) => {
    if (!clienteId) return '';
    const cliente = clientes.find(c => (c.id || c._id) === clienteId);
    return cliente ? cliente.nombre : 'Cliente no encontrado';
  };

  const getRolBadge = (rol) => {
    const configs = {
      admin: { 
        bg: 'bg-red-100', 
        text: 'text-red-800', 
        border: 'border-red-200',
        icon: Shield,
        label: 'Administrador' 
      },
      tecnico: { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-800', 
        border: 'border-emerald-200',
        icon: User,
        label: 'Técnico' 
      },
      cliente: { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        border: 'border-green-200',
        icon: Building2,
        label: 'Cliente' 
      }
    };
    
    const config = configs[rol] || configs.tecnico;
    const IconComponent = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    try {
      return new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  // Filtrar usuarios
  const usuariosFiltrados = usuarios.filter(usuario => {
    const matchSearch = !searchTerm || 
      usuario.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchRol = !filterRol || usuario.rol === filterRol;
    
    return matchSearch && matchRol;
  });

  const getEstadisticas = () => {
    const total = usuarios.length;
    const admins = usuarios.filter(u => u.rol === 'admin').length;
    const tecnicos = usuarios.filter(u => u.rol === 'tecnico').length;
    const clientesRol = usuarios.filter(u => u.rol === 'cliente').length;
    
    return { total, admins, tecnicos, clientesRol };
  };

  const estadisticas = getEstadisticas();

  if (user?.rol !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="text-center">
            <AlertTriangle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Restringido</h2>
            <p className="text-gray-600">
              Solo los administradores pueden gestionar usuarios del sistema.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Users className="h-8 w-8 mr-3 text-emerald-600" />
                Gestión de Usuarios
              </h1>
              <p className="text-gray-600 mt-1">
                Administra los usuarios del sistema y sus permisos
              </p>
            </div>
            <button
              onClick={handleNuevoUsuario}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg shadow-lg transition-colors flex items-center space-x-2"
            >
              <UserPlus className="h-5 w-5" />
              <span>Nuevo Usuario</span>
            </button>
          </div>

          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Users className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
                  <p className="text-2xl font-bold text-gray-900">{estadisticas.total}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Shield className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Administradores</p>
                  <p className="text-2xl font-bold text-gray-900">{estadisticas.admins}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <User className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Técnicos</p>
                  <p className="text-2xl font-bold text-gray-900">{estadisticas.tecnicos}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Clientes</p>
                  <p className="text-2xl font-bold text-gray-900">{estadisticas.clientesRol}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por usuario, nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            
            <div className="sm:w-48">
              <select
                value={filterRol}
                onChange={(e) => setFilterRol(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Todos los roles</option>
                <option value="admin">Administradores</option>
                <option value="tecnico">Técnicos</option>
                <option value="cliente">Clientes</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de usuarios */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              <p className="mt-4 text-gray-600">Cargando usuarios...</p>
            </div>
          ) : usuariosFiltrados.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || filterRol ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
              </h3>
              <p className="text-gray-600">
                {searchTerm || filterRol 
                  ? 'Intenta ajustar los filtros de búsqueda'
                  : 'Crea el primer usuario del sistema'
                }
              </p>
            </div>
          ) : (
            <SmartAdaptiveList
              items={usuariosFiltrados}
              headers={['Usuario', 'Rol', 'Estado', 'Cliente Asociado', 'Creado', 'Acciones']}
              renderDesktopRow={(usuario) => (
                <>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-medium">
                          {usuario.nombre_completo?.charAt(0).toUpperCase() || usuario.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {usuario.nombre_completo || usuario.username}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {usuario.username}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {usuario.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRolBadge(usuario.rol)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                      usuario.activo 
                        ? 'bg-green-100 text-green-800 border-green-200' 
                        : 'bg-red-100 text-red-800 border-red-200'
                    }`}>
                      {usuario.activo ? (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Activo
                        </>
                      ) : (
                        <>
                          <Ban className="h-3 w-3 mr-1" />
                          Inactivo
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {usuario.rol === 'cliente' ? (
                      <div className="text-sm text-gray-900">
                        {obtenerNombreCliente(usuario.cliente_id) || (
                          <span className="text-red-500">Sin asignar</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatearFecha(usuario.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditarUsuario(usuario)}
                        className="text-emerald-600 hover:text-emerald-900 p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Editar usuario"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleEstado(usuario)}
                        className={`${
                          usuario.activo ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                        } p-2 rounded-lg transition-colors`}
                        title={usuario.activo ? "Desactivar usuario" : "Activar usuario"}
                      >
                        {usuario.activo ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleEliminarUsuario(usuario)}
                        className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar usuario"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </>
              )}
              renderMobileCard={(usuario) => (
                <div className="space-y-3">
                  {/* Header con Avatar, Nombre y Rol */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-medium shadow-sm">
                        {usuario.nombre_completo?.charAt(0).toUpperCase() || usuario.username?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {usuario.nombre_completo || usuario.username}
                        </div>
                        <div className="text-xs text-gray-500">@{usuario.username}</div>
                      </div>
                    </div>
                    {getRolBadge(usuario.rol)}
                  </div>

                  {/* Detalles */}
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="truncate">{usuario.email}</span>
                    </div>
                    
                    {usuario.rol === 'cliente' && (
                      <div className="flex items-center text-gray-600">
                        <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="truncate">
                          {obtenerNombreCliente(usuario.cliente_id) || <span className="text-red-500">Sin cliente asignado</span>}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                        usuario.activo 
                          ? 'bg-green-50 text-green-700 border-green-200' 
                          : 'bg-red-50 text-red-700 border-red-200'
                      }`}>
                        {usuario.activo ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Activo
                          </>
                        ) : (
                          <>
                            <Ban className="h-3 w-3 mr-1" />
                            Inactivo
                          </>
                        )}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatearFecha(usuario.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => handleEditarUsuario(usuario)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                    >
                      <Edit3 className="h-4 w-4 mr-1.5" />
                      Editar
                    </button>
                    <button
                      onClick={() => handleToggleEstado(usuario)}
                      className={`flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        usuario.activo 
                          ? 'text-orange-700 bg-orange-50 hover:bg-orange-100' 
                          : 'text-green-700 bg-green-50 hover:bg-green-100'
                      }`}
                    >
                      {usuario.activo ? (
                        <>
                          <Ban className="h-4 w-4 mr-1.5" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1.5" />
                          Activar
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleEliminarUsuario(usuario)}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            />
          )}
        </div>

        {/* Información adicional */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
        </div>
      </div>

      {/* Modal de formulario */}
      {showForm && (
        <UsuarioForm
          usuario={editingUser}
          onGuardado={handleFormSave}
          onCancelar={handleFormClose}
        />
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-100 p-3 rounded-full mr-4">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Confirmar eliminación</h3>
                  <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                ¿Estás seguro de que deseas eliminar al usuario{' '}
                <span className="font-medium">"{userToDelete.nombre_completo || userToDelete.username}"</span>?
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarEliminacion}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar Usuario
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}